import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Ban,
  CalendarClock,
  FileWarning,
  ShieldAlert,
  Sparkles,
  Star,
  TrendingUp,
} from "lucide-react";

const STAGES = [
  { key: "submitted",   label: "Submitted",     color: "#94A3B8", icon: FileWarning },
  { key: "filed",       label: "Filed",         color: "#D4AF37", icon: FileWarning },
  { key: "escalated",   label: "Escalated",     color: "#D4AF37", icon: ShieldAlert },
  { key: "removed",     label: "Removed",       color: "#10B981", icon: BadgeCheck },
];

function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24)));
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (d <= 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 30) return `${d} days ago`;
  const m = Math.floor(d / 30);
  return m === 1 ? "1 month ago" : `${m} months ago`;
}

export default function ProgressDashboard({ cases = [], summary }) {
  const {
    counts,
    total,
    activeTotal,
    avgDaysToRemove,
    activityFeed,
    ratingSeries,
    projectedDelta,
  } = useMemo(() => {
    const counts = { submitted: 0, filed: 0, escalated: 0, removed: 0, not_eligible: 0 };
    cases.forEach((c) => {
      if (counts[c.status] !== undefined) counts[c.status] += 1;
    });
    const total = cases.length;
    const activeTotal =
      counts.submitted + counts.filed + counts.escalated + counts.removed;

    // Avg days to remove for cases with a resolved_at
    const removed = cases.filter((c) => c.status === "removed" && c.resolved_at);
    const avgDaysToRemove = removed.length
      ? Math.round(
          removed
            .map((c) => daysBetween(c.created_at, c.resolved_at))
            .filter((v) => v != null)
            .reduce((s, v, _, arr) => s + v / arr.length, 0)
        )
      : null;

    // Activity feed = every timeline entry across all cases, sorted desc, capped to 8
    const feed = [];
    cases.forEach((c) => {
      (c.timeline || []).forEach((t) => {
        if (!t?.at) return;
        feed.push({
          at: t.at,
          label: t.label,
          detail: t.detail,
          caseId: c.id,
          reason: c.reason,
        });
      });
    });
    feed.sort((a, b) => new Date(b.at) - new Date(a.at));
    const activityFeed = feed.slice(0, 8);

    // Rating projection — simple model:
    // baseline 3.2, each removed review adds +0.3, cap at 4.9
    // series of 8 points using cumulative removals ordered by resolved_at asc
    const removedAsc = [...removed].sort(
      (a, b) => new Date(a.resolved_at) - new Date(b.resolved_at)
    );
    const baseline = 3.2;
    const series = [{ x: 0, y: baseline }];
    removedAsc.forEach((c, i) => {
      const y = Math.min(4.9, baseline + (i + 1) * 0.3);
      series.push({ x: i + 1, y });
    });
    // Extend to at least 4 points so the sparkline draws something
    while (series.length < 4) series.push({ x: series.length, y: series[series.length - 1].y });
    const projectedDelta = +(series[series.length - 1].y - baseline).toFixed(1);

    return {
      counts,
      total,
      activeTotal,
      avgDaysToRemove,
      activityFeed,
      ratingSeries: series,
      projectedDelta,
    };
  }, [cases]);

  // Empty state
  if (total === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="mt-8"
      data-testid="progress-dashboard"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold">Removal progress</h2>
          <p className="text-white/50 text-sm mt-1">
            Live view of every case across the pipeline · updated in real time.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[#10B981]/30 bg-[#10B981]/[0.08] px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold text-[#10B981]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
          Live
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        {/* Pipeline funnel */}
        <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
              Pipeline
            </div>
            <div className="text-[11px] text-white/45">
              {activeTotal} eligible · {counts.not_eligible} not eligible
            </div>
          </div>

          <div className="space-y-3">
            {STAGES.map((s, i) => {
              const value = counts[s.key];
              const pct = activeTotal ? (value / activeTotal) * 100 : 0;
              const Icon = s.icon;
              return (
                <div key={s.key} className="group">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <div className="flex items-center gap-2 text-white/70 font-semibold">
                      <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                      {s.label}
                    </div>
                    <div className="tabular-nums text-white/60">
                      <span className="font-display text-white font-bold text-sm">
                        {value}
                      </span>
                      <span className="text-white/40 ml-1">
                        · {activeTotal ? pct.toFixed(0) : 0}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                      className="h-full rounded-full"
                      style={{
                        background: `linear-gradient(90deg, ${s.color}88, ${s.color})`,
                        boxShadow: pct > 0 ? `0 0 12px ${s.color}55` : "none",
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {counts.not_eligible > 0 && (
              <div className="pt-2 flex items-center justify-between text-[11px] text-white/45 border-t border-white/5 mt-4">
                <span className="inline-flex items-center gap-2">
                  <Ban className="w-3.5 h-3.5" />
                  {counts.not_eligible} case{counts.not_eligible === 1 ? "" : "s"} closed as not eligible
                </span>
                <span className="text-[#10B981] font-semibold">No charge</span>
              </div>
            )}
          </div>

          {/* Quick stats row */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <QuickStat
              icon={<CalendarClock className="w-3.5 h-3.5" />}
              label="Avg. days to remove"
              value={avgDaysToRemove == null ? "—" : `${avgDaysToRemove}d`}
              accent="gold"
            />
            <QuickStat
              icon={<TrendingUp className="w-3.5 h-3.5" />}
              label="Projected rating lift"
              value={projectedDelta > 0 ? `+${projectedDelta.toFixed(1)}★` : "—"}
              accent="emerald"
            />
            <QuickStat
              icon={<BadgeCheck className="w-3.5 h-3.5" />}
              label="Confirmed removals"
              value={counts.removed}
              accent="emerald"
            />
          </div>
        </div>

        {/* Rating projection */}
        <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-gradient-to-br from-[#10B981]/[0.06] via-white/[0.02] to-transparent p-5 sm:p-6 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -bottom-16 -right-10 w-48 h-48 rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none"
          />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
              Rating projection
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-4xl font-bold tabular-nums text-white leading-none">
                {ratingSeries[ratingSeries.length - 1].y.toFixed(1)}
              </span>
              <div className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${
                      ratingSeries[ratingSeries.length - 1].y >= i + 0.6
                        ? "fill-[#D4AF37] text-[#D4AF37]"
                        : "fill-white/10 text-white/15"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="mt-1 text-[11px] text-white/50">
              Baseline 3.2★ · projected delta{" "}
              <span className="text-[#10B981] font-semibold tabular-nums">
                {projectedDelta > 0 ? `+${projectedDelta.toFixed(1)}` : "0.0"}
              </span>
            </div>

            {/* Sparkline */}
            <Sparkline series={ratingSeries} />

            <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
              <Sparkles className="w-3 h-3" />
              +0.3★ estimated per removed review
            </div>
          </div>
        </div>

        {/* Activity feed */}
        <div className="lg:col-span-12 rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="progress-activity">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
              <Activity className="w-3.5 h-3.5" />
              Recent activity
            </div>
            <div className="text-[11px] text-white/40">Last {activityFeed.length} updates</div>
          </div>
          {activityFeed.length === 0 ? (
            <div className="text-white/45 text-sm py-4">
              No activity yet. Once your first case is filed, updates will land here.
            </div>
          ) : (
            <ol className="relative border-l border-white/10 ml-2 space-y-4 pl-5">
              {activityFeed.map((a, i) => {
                const isRemoval = /removed/i.test(a.label);
                const isEscalation = /escalat/i.test(a.label);
                const dotColor = isRemoval
                  ? "#10B981"
                  : isEscalation
                  ? "#D4AF37"
                  : "#94A3B8";
                return (
                  <li key={i} className="relative">
                    <span
                      className="absolute -left-[26px] top-1.5 w-2.5 h-2.5 rounded-full"
                      style={{
                        background: dotColor,
                        boxShadow: `0 0 8px ${dotColor}66`,
                      }}
                    />
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-sm text-white font-semibold">
                        {a.label}
                        <span className="ml-2 text-[11px] font-normal text-white/40">
                          · {a.reason}
                        </span>
                      </div>
                      <div className="text-[11px] tabular-nums text-white/40">
                        {timeAgo(a.at)}
                      </div>
                    </div>
                    {a.detail && (
                      <div className="mt-0.5 text-xs text-white/55 leading-relaxed">
                        {a.detail}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          <a
            href="#cases-list"
            className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-[#10B981] hover:text-[#0ea774] transition-colors"
          >
            Jump to case list
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </motion.section>
  );
}

function QuickStat({ icon, label, value, accent }) {
  const map = {
    emerald: "text-[#10B981]",
    gold: "text-[#D4AF37]",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
      <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${map[accent] || "text-white/60"}`}>
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display font-bold text-white text-lg tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function Sparkline({ series }) {
  const w = 240;
  const h = 60;
  const min = 3.0;
  const max = 5.0;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const points = series.map((p, i) => {
    const x = i * step;
    const y = h - ((p.y - min) / (max - min)) * h;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const areaPath = `${path} L ${points[points.length - 1][0]} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full mt-4 h-16">
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={areaPath}
        fill="url(#sparkFill)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      />
      <motion.path
        d={path}
        fill="none"
        stroke="#10B981"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />
      {points.map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={2.5}
          fill={i === points.length - 1 ? "#D4AF37" : "#10B981"}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.6 + i * 0.06 }}
        />
      ))}
    </svg>
  );
}
